'use client';

import React from 'react';
import { useNode } from '@craftjs/core';
import { motion } from 'framer-motion';
import { Shield, Clock, CreditCard, Award, Headphones, Truck, Star, Zap } from 'lucide-react';
import { isBlankHtml } from '@/utils/functions';
import { AUTO_FIT_ROW, autoFitItem } from '../../utils/autoFitRow';
import { htmlSeguro } from '../../utils/htmlSeguro';
import { AreaDePiezas, Pieza } from '../../utils/piezaLibre';
import { useTranslation } from '@/i18n/hooks/useTranslation';

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string; style?: React.CSSProperties }>> = {
  Shield, Clock, CreditCard, Award, Headphones, Truck, Star, Zap,
};

interface FeatureItem {
  icon: string;
  title: string;
  description: string;
}

interface FeatureShowcaseProps {
  eyebrowText?: string;
  sectionTitle?: string;
  subtitle?: string;
  features?: FeatureItem[];
  bgColor?: string;
  textColor?: string;
  accentColor?: string;
}

function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return `rgba(255,255,255,${alpha})`;
  const num = parseInt(clean, 16);
  return `rgba(${(num >> 16) & 0xff},${(num >> 8) & 0xff},${num & 0xff},${alpha})`;
}

export const FeatureShowcase = ({
  eyebrowText = 'Por qué elegirnos',
  sectionTitle = 'Una experiencia premium en cada detalle',
  subtitle = 'Nos dedicamos a ofrecer el más alto estándar en cada paso del proceso.',
  features = [
    { icon: 'Shield', title: 'Garantía extendida', description: 'Cada vehículo cuenta con cobertura completa respaldada por los mejores estándares de la industria.' },
    { icon: 'CreditCard', title: 'Financiamiento flexible', description: 'Planes personalizados con las mejores tasas del mercado.' },
    { icon: 'Clock', title: 'Atención 24/7', description: 'Soporte dedicado cuando lo necesites, sin importar la hora.' },
    { icon: 'Award', title: 'Certificación premium', description: 'Cada vehículo supera 150 puntos de inspección rigurosa.' },
    { icon: 'Truck', title: 'Entrega a domicilio', description: 'Tu vehículo llega directamente a tu puerta.' },
    { icon: 'Headphones', title: 'Asesor personal', description: 'Un experto dedicado exclusivamente a tu búsqueda.' },
  ],
  bgColor = '#0a0a0a',
  textColor = '#ffffff',
  accentColor = '',
}: FeatureShowcaseProps) => {
  const { connectors } = useNode();
  const { t } = useTranslation();
  const accent = accentColor || '#3b82f6';

  // Bento layout: first card is large (spans 2 rows), rest are normal
  const gridAreas = [
    'sm:col-span-2 sm:row-span-2',  // featured large card
    '', '', '', '', '',
  ];

  // El bento (primera tarjeta 2x2) solo cierra sin huecos con 6, 9, 12…
  // tarjetas. Con cualquier otra cantidad el destacado dejaba celdas vacías,
  // así que se cae a una fila pareja y centrada.
  const useBento = features.length >= 6 && features.length % 3 === 0;
  const itemClass = autoFitItem([{ cols: 1 }, { bp: 'sm', cols: 2 }, { bp: 'lg', cols: 3 }]);

  return (
    <div
      ref={(el: HTMLDivElement | null) => { if (el) connectors.connect(el); }}
      style={{ backgroundColor: bgColor, color: textColor, position: 'relative', overflow: 'hidden' }}
      className="w-full"
    >
      {/* Subtle ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${hexToRgba(accent, 0.06)} 0%, transparent 70%)` }} />
      <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${hexToRgba(accent, 0.04)} 0%, transparent 70%)` }} />

      <AreaDePiezas className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-20"
        >
          <Pieza clave="antetitulo" tipo="texto" etiqueta="Antetítulo">
            {!isBlankHtml(eyebrowText) && (
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px flex-1 max-w-[40px]" style={{ backgroundColor: accent }} />
                <p
                  className="text-xs font-semibold uppercase tracking-[0.25em]"
                  style={{ color: accent }}
                  dangerouslySetInnerHTML={htmlSeguro(eyebrowText)}
                />
              </div>
            )}
          </Pieza>
          <Pieza clave="titulo" tipo="texto" etiqueta="Título">
            {!isBlankHtml(sectionTitle) && (
              <h2
                className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.1] mb-5"
                style={{ color: textColor, letterSpacing: '-0.03em' }}
                dangerouslySetInnerHTML={htmlSeguro(sectionTitle)}
              />
            )}
          </Pieza>
          <Pieza clave="subtitulo" tipo="texto" etiqueta="Descripción">
            {!isBlankHtml(subtitle) && (
              <p
                className="text-lg leading-relaxed max-w-2xl"
                style={{ color: hexToRgba(textColor, 0.5) }}
                dangerouslySetInnerHTML={htmlSeguro(subtitle)}
              />
            )}
          </Pieza>
        </motion.div>

        <Pieza clave="tarjetas" etiqueta="Tarjetas">
          {/* Bento grid */}
          <div
            className={
              useBento
                ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5'
                : `${AUTO_FIT_ROW} gap-4 lg:gap-5 [--bg-gap:1rem] lg:[--bg-gap:1.25rem]`
            }
          >
            {features.map((f, i) => {
              const IconComp = ICON_MAP[f.icon] || Shield;
              const isFeatured = useBento && i === 0;
              const gridClass = useBento ? gridAreas[i] || '' : itemClass;

              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.08 * i }}
                  className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${gridClass} ${isFeatured ? 'p-10 lg:p-12' : 'p-7 lg:p-8'}`}
                  style={{
                    backgroundColor: hexToRgba(textColor, 0.04),
                    border: `1px solid ${hexToRgba(textColor, 0.07)}`,
                  }}
                >
                  {/* Hover glow effect */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: `radial-gradient(ellipse at 50% 0%, ${hexToRgba(accent, 0.08)} 0%, transparent 60%)` }}
                  />

                  {/* Top accent line on hover */}
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                    style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }}
                  />

                  <div className="relative z-10">
                    {/* Icon */}
                    <div
                      className={`rounded-2xl flex items-center justify-center mb-6 ${isFeatured ? 'w-16 h-16' : 'w-12 h-12'}`}
                      style={{
                        background: `linear-gradient(135deg, ${hexToRgba(accent, 0.15)}, ${hexToRgba(accent, 0.05)})`,
                        border: `1px solid ${hexToRgba(accent, 0.15)}`,
                      }}
                    >
                      <IconComp size={isFeatured ? 28 : 22} style={{ color: accent }} />
                    </div>

                    {/* Title */}
                    {!isBlankHtml(f.title) && (
                      <h3
                        className={`font-bold mb-3 leading-tight ${isFeatured ? 'text-2xl lg:text-3xl' : 'text-lg'}`}
                        style={{ color: textColor, letterSpacing: '-0.02em' }}
                        dangerouslySetInnerHTML={htmlSeguro(f.title)}
                      />
                    )}

                    {/* Description */}
                    {!isBlankHtml(f.description) && (
                      <p
                        className={`leading-relaxed ${isFeatured ? 'text-base lg:text-lg' : 'text-[14px]'}`}
                        style={{ color: hexToRgba(textColor, 0.5) }}
                        dangerouslySetInnerHTML={htmlSeguro(f.description)}
                      />
                    )}

                    {/* Featured card: extra accent element */}
                    {isFeatured && (
                      <div className="mt-8 flex items-center gap-2" style={{ color: accent }}>
                        <span className="text-sm font-medium">{t('site.sections.learnMore')}</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="group-hover:translate-x-1 transition-transform duration-300">
                          <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </Pieza>
      </AreaDePiezas>
    </div>
  );
};

FeatureShowcase.craft = {
  displayName: 'FeatureShowcase',
  props: {
    eyebrowText: 'Por qué elegirnos',
    sectionTitle: 'Una experiencia premium en cada detalle',
    subtitle: 'Nos dedicamos a ofrecer el más alto estándar en cada paso del proceso.',
    features: [
      { icon: 'Shield', title: 'Garantía extendida', description: 'Cada vehículo cuenta con cobertura completa respaldada por los mejores estándares de la industria.' },
      { icon: 'CreditCard', title: 'Financiamiento flexible', description: 'Planes personalizados con las mejores tasas del mercado.' },
      { icon: 'Clock', title: 'Atención 24/7', description: 'Soporte dedicado cuando lo necesites, sin importar la hora.' },
      { icon: 'Award', title: 'Certificación premium', description: 'Cada vehículo supera 150 puntos de inspección rigurosa.' },
      { icon: 'Truck', title: 'Entrega a domicilio', description: 'Tu vehículo llega directamente a tu puerta.' },
      { icon: 'Headphones', title: 'Asesor personal', description: 'Un experto dedicado exclusivamente a tu búsqueda.' },
    ],
    bgColor: '#0a0a0a', textColor: '#ffffff', accentColor: '',
  },
  rules: { canDrag: () => true, canDrop: () => true, canMoveIn: () => false },
};
